import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUp, Plus, Trash2, Upload } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn, Field, Input, Select, Textarea } from "../ui";

const IMAGE_EXTS = ".jpg,.jpeg,.png,.webp,.heic";
const VIDEO_EXTS = ".mp4,.webm";
export const isVideoUrl = (url = "") => /\.(mp4|webm)(\?|$)/i.test(url) || /video/i.test(url);

const SIZE_HINT = {
    image: "Best size 1200 × 1500 px (4:5 portrait) or 1200 × 900 (4:3) · JPG or WEBP · under 400 KB",
    video: "MP4, 1280 × 720 (16:9) · under 20 MB · plays automatically, no sound, holds the last frame",
};

// Image or video for a section — uploaded to our own media library.
export const MediaField = ({ field, value, onChange, disabled }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const [kind, setKind] = useState(isVideoUrl(value) ? "video" : "image");

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await rhm.post("/site/media?folder=Homepage", fd);
            onChange(data.url);
            toast.success(kind === "video" ? "Video uploaded" : "Image uploaded");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Field label={field.label} hint={SIZE_HINT[kind]}>
            <div className="space-y-2">
                {value && (isVideoUrl(value) ? (
                    <video src={value} muted autoPlay playsInline className="w-full h-28 object-cover border border-white/10" />
                ) : (
                    <img src={value} alt="" className="w-full h-28 object-cover border border-white/10" />
                ))}
                <Select value={kind} disabled={disabled} data-testid={`field-${field.key}-kind`}
                    onChange={(e) => setKind(e.target.value)}
                    options={[{ value: "image", label: "Upload an image" }, { value: "video", label: "Upload a video" }]} />
                <div className="flex gap-2">
                    <Input value={value || ""} data-testid={`field-${field.key}`} disabled={disabled}
                        onChange={(e) => onChange(e.target.value)} placeholder="https://… or /api/site/media/…" />
                    <Btn variant="ghost" onClick={() => ref.current?.click()} data-testid={`field-${field.key}-upload`}>
                        <Upload className="w-3.5 h-3.5" /> {busy ? "…" : ""}
                    </Btn>
                    {value && (
                        <Btn variant="ghost" onClick={() => onChange("")} data-testid={`field-${field.key}-clear`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </Btn>
                    )}
                </div>
                <input ref={ref} type="file" hidden accept={kind === "video" ? VIDEO_EXTS : IMAGE_EXTS}
                    onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </div>
        </Field>
    );
};

const asBadge = (b) => (typeof b === "string" ? { text: b } : (b || {}));

export const ColorInput = ({ label, value, fallback, onChange, testId, disabled }) => (
    <label className="block">
        <span className="mono text-[9px] tracking-[0.14em] text-zinc-500">{label}</span>
        <div className="flex gap-1.5 mt-1">
            <input type="color" value={value || fallback} disabled={disabled} data-testid={`${testId}-picker`}
                onChange={(e) => onChange(e.target.value)}
                className="w-9 h-9 bg-transparent border border-white/10 cursor-pointer" />
            <Input value={value || ""} disabled={disabled} data-testid={testId} placeholder={fallback}
                onChange={(e) => onChange(e.target.value)} />
        </div>
    </label>
);

// Badges with their own text, border and background colour.
export const BadgesField = ({ field, value, onChange, disabled }) => {
    const badges = value.map(asBadge);
    const set = (i, patch) => onChange(badges.map((b, j) => (j === i ? { ...b, ...patch } : b)));
    return (
        <Field label={`${field.label} (${badges.length})`} hint="Leave a colour blank to use the theme default">
            <div className="space-y-3">
                {badges.map((b, i) => (
                    <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`badge-${i}`}>
                        <div className="flex items-center gap-2">
                            <Input value={b.text || ""} disabled={disabled} data-testid={`badge-text-${i}`}
                                onChange={(e) => set(i, { text: e.target.value })} placeholder="Badge text" />
                            <button onClick={() => { if (i === 0) return; const l = [...badges]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; onChange(l); }}
                                data-testid={`badge-up-${i}`} className="text-zinc-500 hover:text-white">
                                <ArrowUp className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => onChange(badges.filter((_, j) => j !== i))} disabled={disabled}
                                data-testid={`badge-remove-${i}`} className="text-zinc-500 hover:text-red-400">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2">
                            <ColorInput label="TEXT" value={b.color} fallback="#a1a1aa" disabled={disabled}
                                testId={`badge-color-${i}`} onChange={(v) => set(i, { color: v })} />
                            <ColorInput label="BORDER" value={b.border_color} fallback="#3f3f46" disabled={disabled}
                                testId={`badge-border-${i}`} onChange={(v) => set(i, { border_color: v })} />
                            <ColorInput label="BACKGROUND" value={b.bg_color} fallback="#00000000" disabled={disabled}
                                testId={`badge-bg-${i}`} onChange={(v) => set(i, { bg_color: v })} />
                        </div>
                    </div>
                ))}
                <Btn variant="ghost" onClick={() => onChange([...badges, { text: "New badge" }])} data-testid="badge-add">
                    <Plus className="w-3 h-3 mr-1 inline" /> ADD BADGE
                </Btn>
            </div>
        </Field>
    );
};

const paragraphsOf = (post) => (post?.blocks || [])
    .filter((b) => ["paragraph", "quote", "callout"].includes(b.type) && (b.text || b.html))
    .map((b, i) => {
        const raw = b.text || b.html || "";
        const text = raw.replace(/<[^>]*>/g, "").trim();
        return { key: `${b.id || i}`, text };
    })
    .filter((p) => p.text.length > 30);

// Pick a post, pull one of its paragraphs, then edit the wording if you want.
export const BlogHighlightField = ({ field, value, onChange, disabled }) => {
    const v = value || {};
    const set = (patch) => onChange({ ...v, ...patch });

    const { data: posts = [] } = useQuery({
        queryKey: ["rhm-blog-posts-picker"],
        queryFn: async () => (await rhm.get("/blog/posts")).data,
    });
    const { data: post } = useQuery({
        queryKey: ["rhm-blog-post-picker", v.post_slug],
        queryFn: async () => (await rhm.get(`/blog/posts/${v.post_slug}`)).data,
        enabled: !!v.post_slug,
    });
    const paragraphs = paragraphsOf(post);

    return (
        <div className="space-y-3">
            <Field label="Article" hint="Only published articles appear on the website">
                <Select value={v.post_slug || ""} disabled={disabled} data-testid="highlight-post"
                    onChange={(e) => set({ post_slug: e.target.value, text: "" })}
                    options={[{ value: "", label: "Choose an article…" },
                        ...posts.map((p) => ({ value: p.slug, label: `${p.title}${p.status === "published" ? "" : " (draft)"}` }))]} />
            </Field>
            <Field label="Highlighted passage" hint="Pick a paragraph from the article, then edit it freely">
                <Select value="" disabled={disabled || !paragraphs.length} data-testid="highlight-paragraph"
                    onChange={(e) => e.target.value && set({ text: e.target.value })}
                    options={[{ value: "", label: paragraphs.length ? "Pick a paragraph…" : "No paragraphs found" },
                        ...paragraphs.map((p) => ({ value: p.text, label: p.text.slice(0, 70) + (p.text.length > 70 ? "…" : "") }))]} />
            </Field>
            <Field label="Highlight text">
                <Textarea rows={4} value={v.text || ""} disabled={disabled} data-testid="highlight-text"
                    onChange={(e) => set({ text: e.target.value })} placeholder="The line you want people to read" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Show the article title">
                    <Select value={v.show_title === false ? "no" : "yes"} disabled={disabled} data-testid="highlight-show-title"
                        onChange={(e) => set({ show_title: e.target.value === "yes" })}
                        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                </Field>
                <Field label="Show the article image">
                    <Select value={v.show_image === false ? "no" : "yes"} disabled={disabled} data-testid="highlight-show-image"
                        onChange={(e) => set({ show_image: e.target.value === "yes" })}
                        options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                </Field>
            </div>
        </div>
    );
};
